import { Component, Element, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import { MediaQueryDetector } from '../../../../assets/shared/scripts/media-query-detector';
import { mainNavEventEmitterDetails, MenuItem } from './models/main-navigation.types';

@Component({
  tag: 'wink-main-navigation',
  styleUrl: 'styles/main-navigation.default.scss',
  shadow: true,
})
export class MainNavigation {
  @Element() host!: HTMLWinkMainNavigationElement;

  @Prop() menuItems = '';

  @State() _menuItems: MenuItem[] = [];
  @State() isOpen = false;
  @State() activeIndex: number | undefined = undefined;
  @State() selectedIndex: number | undefined = undefined;
  @State() ActiveLinkRef: HTMLAnchorElement | undefined = undefined;

  @Event() mainNavigationItemClicked: EventEmitter<mainNavEventEmitterDetails> | undefined;
  @Event() mainNavigationItemInteracted: EventEmitter<mainNavEventEmitterDetails> | undefined;

  @Listen('flyoutBackLinkClicked', { target: 'document' })
  handleFlyoutBackLinkClicked(e: CustomEvent) {
    const { eventType } = e.detail;

    this.isOpen = false;
    this.activeIndex = undefined;

    if (eventType !== 'mouse') {
      this.ActiveLinkRef?.focus();
    }
  }

  componentWillLoad() {
    if (!this.menuItems) {
      return;
    }
    this._menuItems = JSON.parse(this.menuItems);
  }

  private mainNavigationItemClickEmitter(item: mainNavEventEmitterDetails) {
    this.mainNavigationItemClicked?.emit(item);
  }

  private mainNavigationItemEmitter(item: mainNavEventEmitterDetails) {
    this.mainNavigationItemInteracted?.emit(item);
  }

  private handleFlyOutOpening(item: mainNavEventEmitterDetails) {
    this.isOpen = !!this._menuItems[item.index]?.hasSubNav;
    this.activeIndex = item.index;
    this.mainNavigationItemEmitter(item);
  }

  private handleItemClick(event: MouseEvent | KeyboardEvent, item: mainNavEventEmitterDetails) {
    if (this._menuItems[item.index]?.hasSubNav && MediaQueryDetector.isUntilMdHeader) {
      event.preventDefault();
    }
    this.activeIndex = item.index;
    this.selectedIndex = item.index;
    this.handleFlyOutOpening(item);
    this.mainNavigationItemClickEmitter(item);
  }

  private handleItemMouseOver(item: mainNavEventEmitterDetails) {
    if (item.eventType === 'mouse' && MediaQueryDetector.isUntilMdHeader) {
      return;
    }

    if (this._menuItems[item.index].hasSubNav) {
      this.handleFlyOutOpening(item);
      return;
    }

    this.isOpen = false;
    this.activeIndex = item.index;
    this.mainNavigationItemEmitter({ index: NaN, name: '' });
  }

  private handleKeyDownEvent(e: KeyboardEvent, index: number, name: string) {
    if (e.code === 'Space') {
      e.preventDefault();
      this.handleItemClick(e, { index, name });
    }
  }

  private getATagCssClasses(index: number) {
    return {
      active: this.selectedIndex === index,
    };
  }

  render() {
    return (
      <nav role="navigation">
        <ul role="menu" aria-label="Main navigation menu">
          {this._menuItems.map((item: MenuItem, index: number) => (
            <li role="none">
              <a
                role="menuitem"
                aria-haspopup={(!!item.hasSubNav).toString()}
                aria-expanded={this.activeIndex === index && item.hasSubNav ? 'true' : 'false'}
                aria-controls={item.hasSubNav ? `flyout-${index}` : undefined}
                class={this.getATagCssClasses(index)}
                href={item.url}
                tabindex={0}
                onClick={(event) => this.handleItemClick(event, { index, name: item.name })}
                onMouseOver={() => this.handleItemMouseOver({ index, name: item.name, eventType: 'mouse' })}
                onKeyDown={(e) => this.handleKeyDownEvent(e, index, item.name)}
                ref={(el) => this.activeIndex === index && (this.ActiveLinkRef = el)}>
                {item.name}
                {item.hasSubNav && <wink-icon appearance="default" icon="chevron-right" size="sm" />}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    );
  }
}
